import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useEffectOnce } from "@lib-shared/hooks";
import { BusinessPage } from "core/constants";
import { AppContext } from "core/context";
import { logApiError } from "core/lib";
import { userStatus, updateUserDetailsAsync } from "core/lib/get-user-details";
import { ModalAlert } from "core/lib/show-modal";
import { UserDetails } from "core/services/cef/permission.service.types";
import { UserActivityLogger, activityLogArea } from "core/services/user-tracking-helper";
import PagerStore from "core/stores/pager.store";
import UserModalStore from "core/stores/user-modal.store";
import NewUserForm from "./components/new-user-form";
import Pager from "components/pager";
import PleaseWait from "components/please-wait";
import UserCards from "./components/user-cards";
import UserDetailsWrapper from "./user-details-wrapper";

const Users = observer(() => {
    const pagerStore = useLocalObservable(() => new PagerStore<UserDetailsWrapper>(9));
    const modalStore = useLocalObservable(() => new UserModalStore({
        display: false,
        cssClassName: "custom-modal user-management"
    }));
    const appContext = useContext(AppContext);
    const [shouldShowUsers, setShouldShowUsers] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);;

    let user = appContext.user;

    const pageTitle = "Users";

    const navigate = useNavigate();

    useEffectOnce(() => {
        if (!user?.can.inviteOrApprove) {
            ModalAlert.permissionsError(() => navigate(BusinessPage.dashboard));
            return;
        }

        appContext.updateHeader(pageTitle);

        new UserActivityLogger(activityLogArea.users).read();

        setDataSource();
    });

    const setDataSource = (showUsers = true) => {
        const users = showUsers
            ? user?.company?.childUsers?.filter(x => x.status !== userStatus.deleted)
            : user?.company?.incompleteProfiles;

        const items = users?.map((x: UserDetails) => new UserDetailsWrapper(x))
            .sort((a: UserDetailsWrapper, b: UserDetailsWrapper) => {
                if (a.fullName < b.fullName) {
                    return -1;
                }

                if (a.fullName > b.fullName) {
                    return 1;
                }

                return 0;
            });
        pagerStore.updateItems(items ?? []);

        setShouldShowUsers(showUsers);
    }

    const getLatestUserPermissions = async () => {
        //Note: Updating users can be slow. 
        //Would be better to have an api endpoint that 
        //just returns the users for a specific company.
        setIsLoading(true);

        try {
            await updateUserDetailsAsync(true);

            user = appContext.user;

            setDataSource(shouldShowUsers);
        } catch (error) {
            logApiError(error);
        }

        setIsLoading(false);
    }

    const subNavLink = (name: string, showUsers: boolean) => {
        const isActive = showUsers === shouldShowUsers;
        const className = isActive ? "active" : "";

        return (
            <li className={className} onClick={(e) => setDataSource(showUsers)}>{name}</li>
        );
    }

    const UserManagementMenu = () => {
        return <div className="user-management-menu">
            <ul className="dashboard-navigation">
                {subNavLink("User Permissions", true)}

                {subNavLink("Pending Invites", false)}
            </ul>

            <div className="user-update">
                {/* <button type="button" title="Transfer access">
                        <i className="icon-guest-checkout"></i>
                    </button> */}

                <button type="button" title="Add new user" onClick={() => modalStore.updateDisplay(true)}>
                    <i className="icon-user-plus"></i>
                </button>
            </div>
        </div>;
    }

    const AddUser = () => {
        if (pagerStore.items?.length) {
            return null;
        }

        if (!shouldShowUsers) {
            return (
                <ul className="info-rows no-users">
                    <li>
                        <span>
                            <h5>
                                <i className="icon-fix-errors" />
                                There no pending users
                            </h5>
                        </span>
                    </li>
                </ul>
            );
        }

        return (
            <ul className="info-rows no-users">
                <li>
                    <span>
                        <h5>
                            <i className="icon-fix-errors" />
                            There are no users. Would you like to add one?
                        </h5>
                    </span>

                    <button
                        className="btn btn-primary"
                        onClick={() => modalStore.updateDisplay(true)}>
                        Add New User
                    </button>
                </li>
            </ul>
        );
    }

    if (isLoading) {
        return (<PleaseWait message="Updating users list" />);
    }

    return (
        <div className="user-management floaty-form condensed user-form">
            <UserManagementMenu />

            <UserCards
                users={pagerStore.pageItems}
                modalStore={modalStore}
                isPendingUsers={!shouldShowUsers} />

            <AddUser />

            <Pager
                isLoading={!pagerStore.hasLoaded}
                currentItems={pagerStore.pageItems}
                pageCount={pagerStore.numPages}
                currentPage={pagerStore.currentPage}
                handlePageClick={(e: any) => {
                    pagerStore.updateCurrentPage(e.selected)
                }} />

            <NewUserForm
                id={"new-user-form"}
                onUpdate={getLatestUserPermissions}
                modalStore={modalStore}
            />
        </div>
    );
});

export default Users;
